import { type BrandConfig } from 'constants/branding';

/**
 * The pure half of the reseller branding layer: merging a provider's identity
 * over a resolved brand, and deciding whether a nav path is within a portal's
 * allowance.
 *
 * <p>Kept out of the React context on purpose. The context imports the API
 * service, which imports axios — so anything reaching these functions through it
 * drags a network client into a unit test. These are the rules; the context is
 * only the plumbing that feeds them.
 */

/** The identity fields a provider profile may override. All optional. */
export interface PortalBrandOverrides {
  applies?: boolean;
  brandName?: string;
  brandOwner?: string;
  brandTagline?: string;
  logoUrl?: string;
  footerCompanyName?: string;
  showPoweredBy?: boolean;
}

/**
 * Merge only the keys the profile actually carries.
 *
 * <p>`??` and not `||` throughout: an explicit `false` on `showPoweredBy` has to
 * survive, or "hide powered by" is not expressible. An absent key means "keep
 * what the hostname resolved", which is what lets a reseller set only a name and
 * still get a complete, coherent brand.
 */
export function mergeBrand(base: BrandConfig, p: PortalBrandOverrides | null): BrandConfig {
  if (!p || !p.applies) return base;
  return {
    ...base,
    brandName: p.brandName ?? base.brandName,
    brandOwner: p.brandOwner ?? base.brandOwner,
    brandTagline: p.brandTagline ?? base.brandTagline,
    logo: { ...base.logo, imageUrl: p.logoUrl ?? base.logo.imageUrl },
    footer: {
      ...base.footer,
      companyName: p.footerCompanyName ?? base.footer.companyName,
      showPoweredBy: p.showPoweredBy ?? base.footer.showPoweredBy,
    },
  };
}

/**
 * Whether a path is inside the portal's allowance.
 *
 * <p><b>An empty allowlist allows everything.</b> That is the safety property
 * the whole feature rests on: "not configured" must never mean "hide
 * everything", or a profile created before anyone chose the tab list blanks a
 * customer's portal.
 *
 * <p>Matching is on path SEGMENTS, not string prefixes — `/agents` must not
 * unlock `/agentsomething`, which a `startsWith` alone would, quietly widening
 * every reseller's portal.
 */
export function pathIsAllowed(path: string, allowlist: string[]): boolean {
  if (allowlist.length === 0) return true;
  const p = normalise(path);
  return allowlist.some((raw) => {
    const a = normalise(raw);
    // '/' matches ONLY '/'. Treating it as a prefix would unlock the entire
    // portal — and '/' is the Overview page, so it is the first entry anybody
    // adds to an allowlist. A root that silently allows everything is an
    // allowlist that quietly does nothing, which is worse than not having one.
    if (a === '/') return p === '/';
    return p === a || p.startsWith(a + '/');
  });
}

const normalise = (s: string) => (s || '').toLowerCase().replace(/\/+$/, '') || '/';
